import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Search, Stethoscope, Settings2 } from "lucide-react";
import { DoctorFormDialog } from "@/components/doctors/DoctorFormDialog";

export default function Doctors() {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(false);

  useEffect(() => { api.get("/services").then(({ data }) => setServices(data.services)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = search.trim() ? { search: search.trim() } : {};
    try { const { data } = await api.get("/doctors", { params }); setDoctors(data.doctors); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="animate-fade-up space-y-6" data-testid="doctors-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">Doctors</h1>
          <p className="mt-1 text-sm text-slate-500"><span className="font-medium text-slate-700">{doctors.length}</span> practitioners</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/services")} data-testid="manage-services-button"><Settings2 className="h-4 w-4" /> Services</Button>
          <Button onClick={() => setDialog(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-doctor-button"><Plus className="h-4 w-4" /> Add Doctor</Button>
        </div>
      </div>

      <Card className="p-4 shadow-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by name or specialization…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="doctors-search" />
        </div>
      </Card>

      <Card className="hidden overflow-hidden shadow-sm md:block">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-200 hover:bg-transparent">
              <TableHead className="pl-6 text-xs uppercase tracking-wider text-slate-400">Doctor</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Department</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Fee</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Slot</TableHead>
              <TableHead className="pr-6 text-right text-xs uppercase tracking-wider text-slate-400">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? [...Array(4)].map((_, i) => <TableRow key={i}><TableCell colSpan={5} className="px-6"><Skeleton className="h-8 w-full" /></TableCell></TableRow>)
              : doctors.map((d) => (
                <TableRow key={d.id} className="cursor-pointer border-slate-100" onClick={() => navigate(`/doctors/${d.id}`)} data-testid="doctor-row">
                  <TableCell className="pl-6">
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><Stethoscope className="h-4 w-4" /></div>
                      <div><p className="font-medium text-slate-900">{d.name}</p><p className="text-xs text-slate-400">{d.specialization}</p></div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{d.department || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-600">{d.consultation_fee ? `₹${d.consultation_fee}` : "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-600">{d.slot_duration}m</TableCell>
                  <TableCell className="pr-6 text-right">
                    <Badge className={d.is_active ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" : "bg-slate-100 text-slate-500"}>{d.is_active ? "Active" : "Inactive"}</Badge>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      <div className="space-y-3 md:hidden">
        {doctors.map((d) => (
          <Card key={d.id} className="p-4 shadow-sm" onClick={() => navigate(`/doctors/${d.id}`)} data-testid="doctor-card">
            <p className="font-medium text-slate-900">{d.name}</p>
            <p className="text-xs text-slate-500">{d.specialization} · {d.department}</p>
          </Card>
        ))}
      </div>

      <DoctorFormDialog open={dialog} onOpenChange={setDialog} doctor={null} services={services} onSaved={load} />
    </div>
  );
}
